// SettingsLegal — Phase 5 Plan 05-04 Task 3 (D-89).
// Phase 12 Plan 12-10: migrated to @telegram-apps/telegram-ui (List+Section+Cell).
//
// Owner-only screen listing 7 doc_types (driver_pp / dispatcher_pp /
// customer_pp / dpia / dpa / scc / lia) with [Download PDF] (external browser
// via openLink — ISSUE-07) + [View MD inline] (modal с renderLegalMarkdown).
//
// Layout: sectioned — user-facing PP (3) + internal compliance (4).
// Owner-only gating preserved (Security V4, T-12-10-03).

import { Button, Cell, IconButton, List, Section } from "@telegram-apps/telegram-ui";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { type LegalDocType, type LegalLocale, legalApi } from "../api/legal";
import { useAuth } from "../auth/useAuth";
import { decodeJwtPayload } from "../lib/jwt";
import { renderLegalMarkdown } from "../lib/legalRedacted";
import { SPACING, TOUCH_TARGET_MIN, TYPOGRAPHY } from "../lib/tokens";

interface DocCard {
  docType: LegalDocType;
  version: string;
  labelKey: string;
  locales: LegalLocale[];
  section: "user_facing" | "internal";
}

const DOC_CARDS: ReadonlyArray<DocCard> = [
  // User-facing PP v2.0
  {
    docType: "driver",
    version: "2.0",
    labelKey: "legal.driver_pp",
    locales: ["tr", "bg", "ru"],
    section: "user_facing",
  },
  {
    docType: "dispatcher",
    version: "2.0",
    labelKey: "legal.dispatcher_pp",
    locales: ["tr", "bg", "en"],
    section: "user_facing",
  },
  {
    docType: "customer",
    version: "2.0",
    labelKey: "legal.customer_pp",
    locales: ["de", "en", "fr"],
    section: "user_facing",
  },
  // Internal compliance
  {
    docType: "dpia",
    version: "1.0",
    labelKey: "legal.dpia",
    locales: ["en"],
    section: "internal",
  },
  {
    docType: "dpa",
    version: "1.0",
    labelKey: "legal.dpa",
    locales: ["en", "de", "fr"],
    section: "internal",
  },
  {
    docType: "scc",
    version: "1.0",
    labelKey: "legal.scc",
    locales: ["en", "tr"],
    section: "internal",
  },
  {
    docType: "lia",
    version: "1.0",
    labelKey: "legal.lia",
    locales: ["en"],
    section: "internal",
  },
];

interface TelegramWebAppLike {
  openLink?: (url: string, options?: { try_instant_view?: boolean }) => void;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebAppLike };
  }
}

interface SettingsLegalProps {
  baseUrl: string;
}

interface ViewerState {
  docType: LegalDocType;
  locale: LegalLocale;
  version: string;
  markdown: string;
}

export function SettingsLegal({ baseUrl }: SettingsLegalProps): React.JSX.Element {
  const { t } = useTranslation("settings_legal");
  const { jwt } = useAuth();
  const role = useMemo(() => decodeJwtPayload(jwt)?.role ?? null, [jwt]);
  const isOwner = role === "owner";

  const [viewer, setViewer] = useState<ViewerState | null>(null);
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const destructiveColor =
    "var(--tgui--destructive_text_color, var(--tg-theme-destructive-text-color, red))";

  if (!isOwner) {
    return (
      <List>
        <Section>
          <Cell role="alert" multiline style={{ color: destructiveColor }}>
            {t("owner_only")}
          </Cell>
        </Section>
      </List>
    );
  }

  const downloadPdf = (docType: LegalDocType, locale: LegalLocale, version: string): void => {
    const url = legalApi.getPdfUrl(baseUrl, docType, locale, version);
    // ISSUE-07 enforcement — open in external browser
    const tg = window.Telegram?.WebApp;
    if (tg?.openLink) {
      tg.openLink(url);
    } else {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const viewMd = async (
    docType: LegalDocType,
    locale: LegalLocale,
    version: string,
  ): Promise<void> => {
    const key = `${docType}-${locale}-${version}`;
    setLoading(key);
    setError(null);
    try {
      const res = await legalApi.getDocMarkdown(baseUrl, docType, locale, version);
      setViewer({
        docType,
        locale,
        version,
        markdown: res.markdown,
      });
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(null);
    }
  };

  const renderSection = (section: "user_facing" | "internal") => (
    <Section header={t(section === "user_facing" ? "section.user_facing" : "section.internal")}>
      {DOC_CARDS.filter((c) => c.section === section).map((card) => (
        <Cell
          key={card.docType}
          data-testid={`legal-card-${card.docType}`}
          multiline
          subtitle={`${t("locales_label")}: ${card.locales.join(", ")} · v${card.version}`}
        >
          <div>{t(card.labelKey)}</div>
          <div
            style={{
              display: "flex",
              gap: SPACING.sm,
              marginTop: SPACING.sm,
              flexWrap: "wrap",
            }}
          >
            {card.locales.map((loc) => (
              <div key={loc} style={{ display: "flex", gap: SPACING.xs }}>
                <Button
                  size="s"
                  mode="filled"
                  onClick={() => downloadPdf(card.docType, loc, card.version)}
                  data-testid={`download-pdf-${card.docType}-${loc}`}
                >
                  {t("download_pdf")} {loc.toUpperCase()}
                </Button>
                <Button
                  size="s"
                  mode="bezeled"
                  onClick={() => viewMd(card.docType, loc, card.version)}
                  loading={loading === `${card.docType}-${loc}-${card.version}`}
                  disabled={loading === `${card.docType}-${loc}-${card.version}`}
                  data-testid={`view-md-${card.docType}-${loc}`}
                >
                  {t("view_md_inline")} {loc.toUpperCase()}
                </Button>
              </div>
            ))}
          </div>
        </Cell>
      ))}
    </Section>
  );

  return (
    <List style={{ maxWidth: 720, margin: "0 auto" }}>
      <Section header={t("title")} footer={t("subtitle")}>
        {error && (
          <Cell role="alert" multiline style={{ color: destructiveColor }}>
            {error}
          </Cell>
        )}
      </Section>

      {renderSection("user_facing")}
      {renderSection("internal")}

      {/* Inline MD viewer (modal-style) */}
      {viewer && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: SPACING.md,
            zIndex: 1000,
          }}
        >
          <div
            data-testid="md-viewer-modal"
            style={{
              background: "var(--tgui--bg_color, var(--tg-theme-bg-color, #fff))",
              borderRadius: 8,
              padding: SPACING.md,
              maxWidth: 680,
              maxHeight: "80vh",
              overflowY: "auto",
              width: "100%",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: SPACING.sm,
              }}
            >
              <h3 style={{ fontSize: TYPOGRAPHY.heading.size, margin: 0 }}>
                {viewer.docType.toUpperCase()} · {viewer.locale.toUpperCase()} · v{viewer.version}
              </h3>
              <IconButton
                mode="plain"
                onClick={() => setViewer(null)}
                aria-label={t("close")}
                style={{ minHeight: TOUCH_TARGET_MIN, minWidth: TOUCH_TARGET_MIN }}
              >
                ×
              </IconButton>
            </div>
            <div style={{ fontSize: TYPOGRAPHY.body.size, lineHeight: 1.6 }}>
              {renderLegalMarkdown(viewer.markdown)}
            </div>
          </div>
        </div>
      )}
    </List>
  );
}
